'use client'

import { useState } from 'react'
import { KanbanCard } from './KanbanCard'
import type { Task } from './KanbanBoard'

// ============================================================================
// Types
// ============================================================================

type ColumnStatus = 'pending' | 'running' | 'needs_review' | 'done'

interface KanbanColumnProps {
  title: string
  status: string
  tasks: Task[]
  onDragStart: (taskId: string) => void
  onDragEnd: () => void
  onDrop: (status: ColumnStatus) => void
  isDragging: boolean
  onTaskClick?: (taskId: string) => void
}

// ============================================================================
// Component
// ============================================================================

export function KanbanColumn({
  title,
  status,
  tasks,
  onDragStart,
  onDragEnd,
  onDrop,
  isDragging,
  onTaskClick
}: KanbanColumnProps) {
  const [isDragOver, setIsDragOver] = useState(false)

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    onDrop(status as ColumnStatus)
  }

  // ============================================================================
  // Render
  // ============================================================================

  const columnColors = {
    pending: 'bg-gray-100 dark:bg-gray-800',
    running: 'bg-blue-50 dark:bg-blue-900/20',
    needs_review: 'bg-yellow-50 dark:bg-yellow-900/20',
    done: 'bg-green-50 dark:bg-green-900/20'
  }

  return (
    <div
      className={`flex flex-col rounded-lg ${columnColors[status as keyof typeof columnColors] || columnColors.pending} transition-all ${
        isDragOver ? 'ring-2 ring-blue-500 ring-offset-2' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <span className="px-2 py-1 text-xs font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
            {tasks.length}
          </span>
        </div>
      </div>

      {/* Tasks */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px]">
        {tasks.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-gray-500 dark:text-gray-400">
            {isDragOver ? '↓ Solte aqui' : 'Nenhuma task'}
          </div>
        ) : (
          tasks.map(task => (
            <KanbanCard
              key={task.id}
              task={task}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onClick={onTaskClick}
            />
          ))
        )}
      </div>
    </div>
  )
}
